import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppHeader, DateBar, SignedOutBar, SiteFooter, type CtaLink, type NavItem } from './chrome';
import { useViewer } from './shell';
import { ChatProvider } from './chat/ChatLauncher';
import { addDays, dateFromPathname, datePath, parseDatePath, sameDay } from './lib/dates';
import './layout.css';

/**
 * The frame around every customer page: header, date bar (or the signed-out
 * bar), the page, footer and gambling-help notice. The date bar moves between
 * days on the home route: /YYYYMMDD.
 */
export function SiteLayout({
  children,
  siteName,
  links,
  dateExtra,
  beforeMain,
  signedOutMessage,
  signedOutActions,
  footerLinks,
  queryDatePaths = [],
}: {
  children: ReactNode;
  siteName: string;
  /** Links for a signed-in viewer (the header shows none when signed out). */
  links: NavItem[];
  /** Extra control in the date bar, e.g. the model selector. */
  dateExtra?: ReactNode;
  /** Rendered between the bars and the page, e.g. an upcoming-tips strip. */
  beforeMain?: ReactNode;
  signedOutMessage?: string;
  signedOutActions: CtaLink[];
  footerLinks: CtaLink[];
  /** Pages that take their date as ?date=YYYYMMDD instead of a /YYYYMMDD path. */
  queryDatePaths?: string[];
}) {
  const viewer = useViewer();
  const location = useLocation();
  const navigate = useNavigate();
  const inQuery = queryDatePaths.includes(location.pathname);
  const selected = (inQuery && parseDatePath(new URLSearchParams(location.search).get('date'))) || dateFromPathname(location.pathname);
  const go = (d: Date) => navigate(inQuery ? `${location.pathname}?date=${datePath(d)}` : `/${datePath(d)}`);
  const long = selected.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const short = selected.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <ChatProvider>
    <div className="ui-site">
      <AppHeader links={viewer.isAuthenticated ? links : []} greeting={viewer.isAuthenticated ? `Welcome, ${viewer.firstName || 'there'}` : undefined} />
      {viewer.isAuthenticated ? (
        <DateBar
          label={long}
          shortLabel={short}
          isToday={sameDay(selected, new Date())}
          onPrev={() => go(addDays(selected, -1))}
          onToday={() => go(new Date())}
          onNext={() => go(addDays(selected, 1))}
          extra={dateExtra}
        />
      ) : (
        <SignedOutBar message={signedOutMessage} actions={signedOutActions} />
      )}
      {beforeMain}
      <main className="ui-main">{children}</main>
      <SiteFooter siteName={siteName} links={footerLinks} />
    </div>
    </ChatProvider>
  );
}
